import * as React from 'react';
import {useRef} from 'react';
import {createView, view, AnimatePresence} from '@sail/engine';
import {useId} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';
import type {PopoverInternalProps} from './utils';
import {BottomSheet} from '../bottom-sheet/BottomSheet';
import {PopoverBottomSheetHeader} from './PopoverBottomSheetHeader';
import {ButtonConfig} from '../Button';
import {SelectConfig} from '../Select';
import {SelectFieldConfig} from '../SelectFieldConfig';
import {useProps} from '../../hooks/useProps';
import {css} from '../../css';
import {popoverContentWrapperResetStyles} from '../../utilStyles/sheet';
import {useScrollOverflow} from '../../hooks/useScrollOverflow';

export const PopoverBottomSheet = createView<PopoverInternalProps>(
  ({
    popoverIsControlled,
    open,
    onOpenChange,
    targetRef,
    placement: reactAriaPlacement,
    offset: offsetOptions,
    flip: flipEnabled,
    shift: shouldShift,
    size: sizeOptions,
    inline: inlineEnabled,
    hide: hideOptions,
    subviews,
    dismissible,
    showOnPress,
    showOnFocus,
    showOnHover,
    bottomSheetTitle,
    title,
    floatingStrategy,
    animate: shouldAnimate = true,
    disableDefaultBottomSheetHeader = false,
    disableDefaultBottomSheetScrollWrapper = false,
    useReactAriaDismissOutside,
    bottomSheetAlwaysUseMaxHeight,
    bottomSheetVariant = 'bottom-sheet',
    children,
    inherits,
    style,
    ...props
  }) => {
    // We ignore showOnPress, showOnFocus, showOnHover, since on mobile there is no hover.
    // It would be worth looking into supporting hover for accessibility in the future.
    useProps(targetRef, {
      onClick: () => {
        if (!popoverIsControlled) {
          onOpenChange(true);
        }
      },
    });

    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const scrollWrapperRef = useRef<HTMLDivElement>(null);

    const {isScrolled, isOverflowing, onScroll} = useScrollOverflow(
      contentWrapperRef,
      scrollWrapperRef,
    );

    const hasTitle = !!(bottomSheetTitle || title);
    const headerId = useId();

    const content = disableDefaultBottomSheetScrollWrapper ? (
      children
    ) : (
      <view.div
        ref={scrollWrapperRef}
        onScroll={onScroll}
        inherits={inherits}
        style={{...style, ...popoverContentWrapperResetStyles}}
        data-testid="popover-scroll-wrapper"
        css={{
          overflowY: 'auto',
          height: 'fill',
        }}
      >
        {children}
      </view.div>
    );

    const layer = (
      <BottomSheet
        aria-labelledby={hasTitle ? headerId : undefined}
        {...props}
        inherits={disableDefaultBottomSheetScrollWrapper ? inherits : undefined}
        style={disableDefaultBottomSheetScrollWrapper ? style : undefined}
        contentWrapperRef={contentWrapperRef}
        requestClose={() => onOpenChange(false)}
        closeConfirmationDialog={null}
        closeConfirmationDialogHeight={null}
        layerName="modal"
        bottomSheetVariant={bottomSheetVariant}
        modal
        shouldAnimate={shouldAnimate}
        subviews={{layer: subviews.layer}}
        // Custom props for the content wrapper
        data-testid={props['data-testid'] ?? 'popover-layer'}
        uses={[
          disableDefaultBottomSheetScrollWrapper ? null : css({stack: 'y'}),
          bottomSheetAlwaysUseMaxHeight && bottomSheetVariant === 'bottom-sheet'
            ? css({'--popover-bottom-sheet-always-use-max-height': '100%'})
            : null,
          isOverflowing
            ? css({
                '--popover-bottom-sheet-footer-border': `1px solid ${$['color.border']}`,
              })
            : css({
                '--popover-bottom-sheet-footer-border': 'none',
              }),
          ButtonConfig.customize({defaults: {size: 'large'}}),
          SelectConfig.customize({defaults: {size: 'large'}}),
          SelectFieldConfig.customize({defaults: {size: 'large'}}),
        ]}
      >
        {disableDefaultBottomSheetHeader && !bottomSheetTitle ? null : (
          <PopoverBottomSheetHeader
            onClose={() => onOpenChange(false)}
            title={bottomSheetTitle || title}
            titleId={headerId}
            css={
              isScrolled
                ? css({borderBlockEnd: `1px solid ${$['color.border']}`})
                : css({borderBlockEnd: 'none'})
            }
          />
        )}
        {content}
      </BottomSheet>
    );

    return <AnimatePresence>{open ? layer : null}</AnimatePresence>;
  },
);
